'use client';

import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  COLOR_SCHEME_COOKIE_KEY,
  COLOR_SCHEME_STORAGE_KEY,
  DEFAULT_COLOR_SCHEME,
  getStoredColorScheme,
  normalizeColorScheme,
  type ColorScheme,
} from '~/shared/configs/ui-preferences';

export type { ColorScheme } from '~/shared/configs/ui-preferences';
export { COLOR_SCHEME_COOKIE_KEY, COLOR_SCHEME_STORAGE_KEY, DEFAULT_COLOR_SCHEME } from '~/shared/configs/ui-preferences';

const COLOR_SCHEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

type ColorSchemeContextValue = Readonly<{
  colorScheme: ColorScheme;
  isDarkTheme: boolean;
  setColorScheme: (scheme: ColorScheme) => void;
  toggleColorScheme: () => void;
}>;

const fallbackContext: ColorSchemeContextValue = {
  colorScheme: DEFAULT_COLOR_SCHEME,
  isDarkTheme: false,
  setColorScheme: () => undefined,
  toggleColorScheme: () => undefined,
};

const ColorSchemeContext = createContext<ColorSchemeContextValue>(fallbackContext);

const getAppliedColorScheme = (): ColorScheme => {
  if (typeof document === 'undefined') return DEFAULT_COLOR_SCHEME;
  return normalizeColorScheme(document.documentElement.dataset.theme);
};

const applyColorScheme = (scheme: ColorScheme): void => {
  if (typeof document === 'undefined') return;

  document.documentElement.dataset.theme = scheme;
  document.documentElement.style.colorScheme = scheme;
  document.body?.setAttribute('data-theme', scheme);
};

const persistColorScheme = (scheme: ColorScheme): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Static-safe source of truth for the next prepaint bootstrap.
  try {
    window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, scheme);
  } catch {
    // Cookie below remains a fallback when localStorage is unavailable.
  }

  document.cookie = `${COLOR_SCHEME_COOKIE_KEY}=${scheme}; Path=/; Max-Age=${COLOR_SCHEME_COOKIE_MAX_AGE}; SameSite=Lax`;
};

type ColorSchemeProviderProps = Readonly<{
  initialColorScheme?: ColorScheme;
  children: ReactNode;
}>;

export const ColorSchemeProvider = ({
  initialColorScheme = DEFAULT_COLOR_SCHEME,
  children,
}: ColorSchemeProviderProps) => {
  // Keep SSR deterministic. Visual theme already comes from html[data-theme]
  // before React hydration, then state is synchronized without repainting the page.
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(initialColorScheme);

  useLayoutEffect(() => {
    // Read persistence directly. The static root HTML always contains the light
    // defaults, so React/Next may temporarily restore those attributes while hydrating.
    // localStorage remains the source of truth for authenticated reloads.
    const persistedScheme = getStoredColorScheme();
    setColorSchemeState(persistedScheme);
    applyColorScheme(persistedScheme);
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== COLOR_SCHEME_STORAGE_KEY || event.newValue === null) return;

      const nextScheme = normalizeColorScheme(event.newValue);
      setColorSchemeState(nextScheme);
      applyColorScheme(nextScheme);
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setColorScheme = useCallback((nextScheme: ColorScheme) => {
    applyColorScheme(nextScheme);
    persistColorScheme(nextScheme);
    setColorSchemeState(nextScheme);
  }, []);

  const toggleColorScheme = useCallback(() => {
    // Read the DOM value so a prepaint-restored dark theme cannot be toggled
    // from stale SSR state before the first effect runs.
    const currentScheme = getAppliedColorScheme();
    setColorScheme(currentScheme === 'dark' ? 'light' : 'dark');
  }, [setColorScheme]);

  const value = useMemo<ColorSchemeContextValue>(
    () => ({
      colorScheme,
      isDarkTheme: colorScheme === 'dark',
      setColorScheme,
      toggleColorScheme,
    }),
    [colorScheme, setColorScheme, toggleColorScheme],
  );

  return createElement(ColorSchemeContext.Provider, { value }, children);
};

const useColorScheme = (): ColorSchemeContextValue => {
  return useContext(ColorSchemeContext);
};

export default useColorScheme;
